import { Paper, Typography, Box, Stack, Chip, Divider } from "@mui/material";

import {
  getMetricDisplayName,
  getPackageDisplayName,
  getPlatformDisplayName,
  getTargetValue,
  getProduct,
} from "@/utils/social";
import { Language, type Maybe } from "@commercial-publishing/shared-types";
import type {
  LinkData,
  Network,
  Product,
} from "@commercial-publishing/shared-types/social";

// Main Summary Component
const SocialSummary = ({
  productId,
  links,
  kpiMetric,
  packageType,
  language,
}: Product) => {
  const product = getProduct(productId);
  const currentTarget =
    getTargetValue(product, packageType, kpiMetric, language || undefined) ??
    undefined;

  const languageDisplay =
    language == null
      ? "Not specified"
      : language === Language.German
        ? "German"
        : "French";

  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        p: 2.5,
        backgroundColor: "grey.50",
        borderRadius: 2,
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          backgroundColor: "grey.100",
          borderColor: "primary.main",
        },
      }}
    >
      <Stack spacing={0}>
        <SummaryField label="Product" value={product.name} />
        <SummaryField label="Metric" value={getMetricDisplayName(kpiMetric)} />
        <SummaryField
          label="Package"
          value={getPackageDisplayName(packageType)}
        />
        <SummaryField label="Language" value={languageDisplay} />
        <PlatformChips platforms={product.platforms} />
        <SocialLinks links={links} />
        <PerformanceMetrics target={currentTarget} />
      </Stack>
    </Paper>
  );
};

// Individual field components for better organization
const SummaryField = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}
    >
      {label}
    </Typography>
    <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
      {value}
    </Typography>
  </Box>
);

const PlatformChips = ({ platforms }: { platforms: Network[] }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        mb: 1,
        display: "block",
      }}
    >
      Platforms
    </Typography>
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {platforms.map((platform) => (
        <Chip
          key={platform}
          label={getPlatformDisplayName(platform)}
          size="small"
          variant="outlined"
          sx={{ fontSize: "0.75rem" }}
        />
      ))}
    </Stack>
  </Box>
);

const PerformanceMetrics = ({ target }: { target: Maybe<number> }) => {
  if (!target) return null;

  return (
    <>
      <Divider sx={{ my: 2 }} />
      <Stack direction="row" spacing={3}>
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            Target
          </Typography>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: "primary.main" }}
          >
            {target.toLocaleString()}
          </Typography>
        </Box>
      </Stack>
    </>
  );
};

const SocialLinks = ({ links }: { links: LinkData[] }) => {
  if (!links?.length) return null;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          mb: 1,
          display: "block",
        }}
      >
        Social Links
      </Typography>
      <Stack spacing={0.5}>
        {links.map(({ socialLink }, index) => (
          <Typography
            key={index}
            variant="body2"
            color={socialLink ? "text.primary" : "text.disabled"}
            sx={{
              fontSize: "0.8rem",
              fontStyle: socialLink ? "normal" : "italic",
              wordBreak: "break-all",
            }}
          >
            {socialLink || `Link ${index + 1} (empty)`}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
};

export default SocialSummary;
